// Product Image Zoom Functionality
console.log('🔍 ProductZoom script loaded');

class ProductZoom {
  constructor() {
    this.isMobile = window.innerWidth < 768;
    this.galleryImages = document.querySelectorAll('.gallery-carousel__cell img');
    this.galleryMedia = document.querySelectorAll('.gallery-carousel__cell');
    this.initialized = false;
    this.zoomLevel = 2.5; // Zoom magnification level
    
    console.log('ProductZoom constructor - isMobile:', this.isMobile, 'window width:', window.innerWidth);
    console.log('Gallery media found:', this.galleryMedia.length);
    
    // Note: init() will be called externally after proper timing
    
    // Handle resize events for responsive behavior
    window.addEventListener('resize', this.handleResize.bind(this));
  }
  
  init() {
    if (this.initialized || !this.galleryMedia.length) {
      console.log('ProductZoom init skipped - initialized:', this.initialized, 'galleryMedia length:', this.galleryMedia.length);
      return;
    }
    
    console.log('ProductZoom initializing...');
    
    this.loadDependencies()
      .then(() => {
        console.log('Dependencies loaded, isMobile:', this.isMobile);
        this.isMobile ? this.initMobileZoom() : this.initDesktopMagnifier();
        this.initialized = true;
      })
      .catch(error => console.error('Failed to initialize product zoom:', error));
  }
  
  loadDependencies() {
    return new Promise((resolve, reject) => {
      if (this.isMobile) {
        // Load PhotoSwipe for mobile experience
        this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe.min.js')
          .then(() => this.loadScript('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe-ui-default.min.js'))
          .then(() => {
            this.loadStyle('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/photoswipe.min.css');
            return this.loadStyle('https://cdnjs.cloudflare.com/ajax/libs/photoswipe/4.1.3/default-skin/default-skin.min.css');
          })
          .then(resolve)
          .catch(reject);
      } else {
        // No need to load medium-zoom anymore since we're using custom magnifier
        resolve();
      }
    });
  }
  
  loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = src;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }
  
  loadStyle(href) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`link[href="${href}"]`)) {
        resolve();
        return;
      }
      
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.onload = resolve;
      link.onerror = reject;
      document.head.appendChild(link);
    });
  }
  
  initDesktopMagnifier() {
    // Set up in-place magnifier zoom for desktop
    this.galleryImages.forEach(img => {
      // Create container for tracking relative mouse position
      const container = img.closest('.gallery-carousel__cell');
      
      // Add zoom cursor on hover
      img.classList.add('zoom-cursor');
      
      // Setup hover effect
      let isZoomed = false;
      
      // Click to toggle zoom
      img.addEventListener('click', (e) => {
        e.preventDefault();
        
        if (!isZoomed) {
          // Enter zoom mode
          isZoomed = true;
          img.classList.add('is-zoomed');
          container.classList.add('is-zooming');
          this.updateZoomedImage(e, img, container);
        } else {
          // Exit zoom mode
          isZoomed = false;
          img.classList.remove('is-zoomed');
          container.classList.remove('is-zooming');
          img.style.transform = 'translate(0, 0) scale(1)';
        }
      });
      
      // Mouse move to update zoom position
      container.addEventListener('mousemove', (e) => {
        if (isZoomed) {
          this.updateZoomedImage(e, img, container);
        }
      });
      
      // Mouse leave to reset zoom
      container.addEventListener('mouseleave', (e) => {
        if (isZoomed) {
          isZoomed = false;
          img.classList.remove('is-zoomed');
          container.classList.remove('is-zooming');
          img.style.transform = 'translate(0, 0) scale(1)';
        }
      });
    });
  }
  
  updateZoomedImage(event, img, container) {
    // Calculate relative position of mouse in container (0 to 1)
    const rect = container.getBoundingClientRect();
    const relX = (event.clientX - rect.left) / rect.width;
    const relY = (event.clientY - rect.top) / rect.height;
    
    // Calculate how much to offset the zoomed image
    // We multiply by (zoomLevel - 1) because at zoom level 2, we need to move 
    // by at most 1x the size in either direction
    const offsetX = (relX - 0.5) * 100 * (this.zoomLevel - 1);
    const offsetY = (relY - 0.5) * 100 * (this.zoomLevel - 1);
    
    // Apply transform with smooth transition
    img.style.transform = `translate(${-offsetX}%, ${-offsetY}%) scale(${this.zoomLevel})`;
  }
  
  initMobileZoom() {
    // Create PhotoSwipe HTML structure if not exists
    if (!document.querySelector('.pswp')) {
      this.createPhotoSwipeHtml();
    }
    
    console.log('Initializing mobile zoom for PhotoSwipe');
    
    // Create items array without waiting for lazy-loaded images
    this.createPhotoSwipeItems();
  }
  
  createPhotoSwipeItems() {
    // Don't wait for lazy-loaded images - use fallback dimensions instead
    console.log('Creating PhotoSwipe items without waiting for lazy load');
    console.log('Gallery media count:', this.galleryMedia.length);
    
    // Prepare items array for PhotoSwipe - handle all media types
    const items = Array.from(this.galleryMedia).map((cell) => {
      const mediaType = cell.getAttribute('data-media-type') || 'image';
      const img = cell.querySelector('img');
      const video = cell.querySelector('video');
      const iframe = cell.querySelector('iframe');
      
      // Handle different media types
      switch(mediaType) {
        case 'video':
          if (video) {
            // Get original video dimensions to calculate aspect ratio
            let originalWidth = video.videoWidth || video.getAttribute('width') || 1920;
            let originalHeight = video.videoHeight || video.getAttribute('height') || 1080;
            
            // Convert string dimensions to numbers
            if (typeof originalWidth === 'string') originalWidth = parseInt(originalWidth);
            if (typeof originalHeight === 'string') originalHeight = parseInt(originalHeight);
            
            // If we still don't have good dimensions, use 16:9 aspect ratio
            if (originalWidth < 100 || originalHeight < 100) {
              originalWidth = 1920;
              originalHeight = 1080; // 16:9 aspect ratio
            }
            
            // Calculate aspect ratio and set to 100vw
            const aspectRatio = originalWidth / originalHeight;
            const viewportWidth = window.innerWidth;
            let videoWidth = viewportWidth;
            let videoHeight = Math.round(viewportWidth / aspectRatio);
            
            console.log('Video dimensions for PhotoSwipe (100vw):', {
              mediaType: 'video',
              originalVideoWidth: video.videoWidth,
              originalVideoHeight: video.videoHeight,
              originalWidth: originalWidth,
              originalHeight: originalHeight,
              aspectRatio: aspectRatio.toFixed(2),
              viewportWidth: viewportWidth,
              finalWidth: videoWidth,
              finalHeight: videoHeight
            });
            
            return {
              html: this.createVideoHtml(video), // Use HTML directly - PhotoSwipe v4 supports this
              w: videoWidth,
              h: videoHeight,
              msrc: img ? img.getAttribute('src') : '', // Use preview image as thumbnail
              isVideo: true,
              originalVideo: video // Keep reference to original video element
            };
          }
          break;
          
        case 'external_video':
          if (iframe) {
            // Set external videos to 16:9 aspect ratio at 100vw
            const aspectRatio = 16 / 9; // Standard video aspect ratio
            const viewportWidth = window.innerWidth;
            const iframeWidth = viewportWidth;
            const iframeHeight = Math.round(viewportWidth / aspectRatio);
            
            console.log('External video dimensions for PhotoSwipe (100vw):', {
              mediaType: 'external_video',
              aspectRatio: aspectRatio.toFixed(2),
              viewportWidth: viewportWidth,
              finalWidth: iframeWidth,
              finalHeight: iframeHeight
            });
            
            return {
              html: this.createExternalVideoHtml(iframe), // Use HTML directly - PhotoSwipe v4 supports this
              w: iframeWidth,
              h: iframeHeight,
              msrc: img ? img.getAttribute('src') : '', // Use preview image as thumbnail
              isVideo: true,
              originalVideo: iframe // Keep reference to original iframe element
            };
          }
          break;
          
        case 'image':
        default:
          if (img) {
            const src = img.getAttribute('src');
            const srcset = img.getAttribute('srcset');
            const highResSrc = this.getHighResolutionImageUrl(src, srcset);
            
            // iOS Safari fix: Use a promise-based approach to get real dimensions
            const viewportWidth = window.innerWidth;
            let width = viewportWidth;
            let height = viewportWidth; // Default to square, will be updated
            
            console.log('🍎 iOS Safari image detection for:', highResSrc);
            
            // Create a temporary image to get real dimensions
            const tempImg = new Image();
            tempImg.crossOrigin = 'anonymous';
            
            const item = {
              src: highResSrc,
              w: width,
              h: height,
              msrc: src,
              needsDimensionUpdate: true // Flag for later update
            };
            
            // Async load to get real dimensions
            tempImg.onload = () => {
              const aspectRatio = tempImg.naturalWidth / tempImg.naturalHeight;
              const newWidth = viewportWidth;
              const newHeight = Math.round(viewportWidth / aspectRatio);
              
              // Only update if dimensions are significantly different
              const widthDiff = Math.abs(item.w - newWidth) / item.w;
              const heightDiff = Math.abs(item.h - newHeight) / item.h;
              
              if (widthDiff > 0.1 || heightDiff > 0.1) {
                item.w = newWidth;
                item.h = newHeight;
                item.needsDimensionUpdate = false;
                
                console.log('✅ Updated real dimensions:', {
                  naturalWidth: tempImg.naturalWidth,
                  naturalHeight: tempImg.naturalHeight,
                  aspectRatio: aspectRatio.toFixed(2),
                  finalWidth: item.w,
                  finalHeight: item.h,
                  isSquare: Math.abs(aspectRatio - 1) < 0.1,
                  updated: true
                });
                
                // Only update PhotoSwipe if it's open and the current item is visible
                if (window.currentPhotoSwipeGallery && window.currentPhotoSwipeGallery.getCurrentIndex) {
                  const currentIndex = window.currentPhotoSwipeGallery.getCurrentIndex();
                  const items = window.currentPhotoSwipeGallery.items;
                  const currentItem = items[currentIndex];
                  
                  // Only update if this is the currently visible item
                  if (currentItem === item) {
                    window.currentPhotoSwipeGallery.invalidateCurrItems();
                    window.currentPhotoSwipeGallery.updateSize(true);
                  }
                }
              } else {
                console.log('✅ Dimensions already correct, no update needed');
              }
            };
            
            tempImg.onerror = () => {
              console.log('⚠️ Could not load image for dimension detection, using fallback');
              const dimensions = this.getImageFallbackDimensions(img);
              const aspectRatio = dimensions.width / dimensions.height;
              item.w = viewportWidth;
              item.h = Math.round(viewportWidth / aspectRatio);
              item.needsDimensionUpdate = false;
            };
            
            tempImg.src = highResSrc;
            
            return item;
          }
          break;
      }
      
      // Fallback for any unhandled cases
      return null;
    }).filter(item => item !== null); // Remove any null items
    
    // Temporarily disable normalization to debug dimension issues
    console.log('Raw PhotoSwipe items before normalization:', items.map(item => ({
      w: item.w,
      h: item.h,
      aspectRatio: (item.w / item.h).toFixed(2),
      type: item.isVideo ? 'video' : 'image'
    })));
    
    // Normalize dimensions across all items to prevent sizing inconsistencies
    // try {
    //   this.normalizeDimensionsV2(items);
    // } catch (error) {
    //   console.error('Error normalizing dimensions:', error);
    // }
    
    // Store items as instance property
    this.photoSwipeItems = items;
    
    // Preload first item if it's an image
    if (items.length > 0 && items[0].src) {
      const preloadImg = new Image();
      preloadImg.src = items[0].src;
    }
    
    // Get Flickity instance to sync index
    const flickityInstance = Flickity.data('.gallery-carousel');
    
    // Add click events to media elements, using event capturing to avoid conflicts
    console.log('Setting up click events for', this.galleryMedia.length, 'media items');
    this.galleryMedia.forEach((cell, index) => {
      // Get the actual media element (img, video, iframe) or the cell itself
      const mediaElement = cell.querySelector('img, video, iframe') || cell;
      
      console.log('Attaching click to:', mediaElement, 'for index:', index);
      
      mediaElement.addEventListener('click', (e) => {
        console.log('Media element clicked:', index, e.target);
        
        // Only proceed if we're on mobile
        if (!this.isMobile) {
          console.log('Not mobile, skipping PhotoSwipe');
          return;
        }
        
        // Only handle clicks on media elements, not on controls
        if (e.target.closest('.flickity-button') || e.target.closest('.thumbnail-item')) {
          console.log('Click on control element, ignoring');
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        
        console.log('Opening PhotoSwipe for clicked index:', index);
        this.openPhotoSwipe(index, this.photoSwipeItems);
      }, true); // Use capture phase to ensure we get the event first
    });
    
    console.log('✅ PhotoSwipe initialization complete with', items.length, 'items');
  }
  
  normalizeDimensions(items) {
    // Find the maximum dimensions to use as a reference
    let maxWidth = 0;
    let maxHeight = 0;
    
    items.forEach(item => {
      if (item.w > maxWidth) maxWidth = item.w;
      if (item.h > maxHeight) maxHeight = item.h;
    });
    
    console.log('PhotoSwipe dimension normalization:', {
      maxWidth,
      maxHeight,
      totalItems: items.length
    });
    
    // Ensure all items have reasonable dimensions relative to the max
    items.forEach((item, index) => {
      const aspectRatio = item.w / item.h;
      
      // If dimensions are significantly smaller than max, scale them up proportionally
      if (item.w < maxWidth * 0.5 || item.h < maxHeight * 0.5) {
        if (aspectRatio > 1) {
          // Landscape: scale based on width
          item.w = Math.max(item.w, maxWidth * 0.8);
          item.h = item.w / aspectRatio;
        } else {
          // Portrait or square: scale based on height
          item.h = Math.max(item.h, maxHeight * 0.8);
          item.w = item.h * aspectRatio;
        }
        
        const newW = Math.round(item.w);
        const newH = Math.round(item.h);
        
        console.log(`Normalized item ${index}:`, {
          originalW: item.w,
          originalH: item.h,
          newW: newW,
          newH: newH,
          aspectRatio
        });
        
        item.w = Math.round(item.w);
        item.h = Math.round(item.h);
      }
    });
  }
  
  async getImageRealDimensions(img) {
    console.log('🔍 Getting REAL dimensions for image:', img.src);
    
    // Try to get dimensions from naturalWidth/Height first (if already loaded)
    if (img.naturalWidth && img.naturalHeight) {
      console.log('✅ Using natural dimensions:', img.naturalWidth, 'x', img.naturalHeight);
      return {
        width: img.naturalWidth,
        height: img.naturalHeight
      };
    }
    
    // Load the actual high-res image to get real dimensions
    const actualImageSrc = this.getHighResolutionImageUrl(img.getAttribute('src'), img.getAttribute('srcset'));
    console.log('🔍 Loading actual image to get real dimensions:', actualImageSrc);
    
    try {
      const realDimensions = await this.loadImageDimensions(actualImageSrc);
      console.log('✅ Got real image dimensions:', realDimensions.width, 'x', realDimensions.height);
      return realDimensions;
    } catch (error) {
      console.log('⚠️ Could not load real image dimensions, using fallback');
      return this.getImageFallbackDimensions(img);
    }
  }
  
  loadImageDimensions(src) {
    return new Promise((resolve, reject) => {
      const testImg = new Image();
      testImg.onload = function() {
        resolve({
          width: this.naturalWidth,
          height: this.naturalHeight
        });
      };
      testImg.onerror = reject;
      testImg.src = src;
    });
  }
  
  getImageFallbackDimensions(img) {
    console.log('🔍 Using fallback dimension detection for:', img.src);
    
    // FIRST: Try to extract dimensions from Shopify URL parameters (most reliable)
    const src = img.getAttribute('src') || img.src;
    console.log('🔍 Analyzing image URL:', src);
    if (src) {
      // Look for dimensions in filename (e.g., image_800x600.jpg)
      const dimensionMatch = src.match(/(\d+)x(\d+)/);
      if (dimensionMatch) {
        const width = parseInt(dimensionMatch[1]);
        const height = parseInt(dimensionMatch[2]);
        console.log('✅ Using filename dimensions:', width, 'x', height);
        return {
          width: width,
          height: height
        };
      }
      
      // Look for width parameter in URL (e.g., ?width=800)
      const widthMatch = src.match(/[?&]width=(\d+)/);
      if (widthMatch) {
        const width = parseInt(widthMatch[1]);
        
        // Try to determine if this should be square or not based on context
        // Large widths (1200+) are often square product images
        // Smaller widths might be rectangular thumbnails
        if (width >= 1200) {
          console.log('✅ Large width URL parameter, assuming square:', width, 'x', width);
          return { 
            width: width, 
            height: width 
          };
        } else {
          // For smaller images, assume a common product image aspect ratio (4:3)
          const height = Math.round(width * 1.25);
          console.log('✅ Small width URL parameter, assuming 4:3 ratio:', width, 'x', height);
          return { 
            width: width, 
            height: height
          };
        }
      }
    }
    
    // SECOND: Try to get from width/height attributes
    const attrWidth = img.getAttribute('width');
    const attrHeight = img.getAttribute('height');
    console.log('🔍 Checking width/height attributes:', attrWidth, 'x', attrHeight);
    if (attrWidth && attrHeight) {
      const width = parseInt(attrWidth);
      const height = parseInt(attrHeight);
      console.log('✅ Using attribute dimensions:', width, 'x', height);
      return {
        width: width,
        height: height
      };
    }
    
    // THIRD: Try to get from data attributes
    const dataWidth = img.getAttribute('data-width');
    const dataHeight = img.getAttribute('data-height');
    console.log('🔍 Checking data attributes:', dataWidth, 'x', dataHeight);
    if (dataWidth && dataHeight) {
      const width = parseInt(dataWidth);
      const height = parseInt(dataHeight);
      console.log('✅ Using data attribute dimensions:', width, 'x', height);
      return {
        width: width,
        height: height
      };
    }
    
    // Final fallback - assume square for product images
    console.log('⚠️ Using final fallback: square 1200 x 1200');
    return {
      width: 1200,
      height: 1200
    };
  }
  
  normalizeDimensionsV2(items) {
    console.log('PhotoSwipe dimension normalization v2 starting...');
    
    // Group items by aspect ratio to handle them differently
    const aspectRatios = items.map(item => ({
      item,
      aspectRatio: item.w / item.h,
      isSquare: Math.abs((item.w / item.h) - 1) < 0.1, // Consider "nearly square" as square
      isLandscape: (item.w / item.h) > 1.2,
      isPortrait: (item.w / item.h) < 0.8
    }));
    
    // Find the largest dimensions for each category
    const maxSquare = Math.max(...aspectRatios.filter(a => a.isSquare).map(a => Math.max(a.item.w, a.item.h)), 0);
    const maxLandscapeW = Math.max(...aspectRatios.filter(a => a.isLandscape).map(a => a.item.w), 0);
    const maxPortraitH = Math.max(...aspectRatios.filter(a => a.isPortrait).map(a => a.item.h), 0);
    
    console.log('Dimension analysis:', {
      maxSquare,
      maxLandscapeW,
      maxPortraitH,
      squareCount: aspectRatios.filter(a => a.isSquare).length,
      landscapeCount: aspectRatios.filter(a => a.isLandscape).length,
      portraitCount: aspectRatios.filter(a => a.isPortrait).length
    });
    
    // Normalize each item based on its category
    aspectRatios.forEach(({item, aspectRatio, isSquare, isLandscape, isPortrait}, index) => {
      const originalW = item.w;
      const originalH = item.h;
      
      if (isSquare && maxSquare > 0) {
        // For square images, ensure they're all the same size as the largest square
        const targetSize = Math.max(maxSquare, 1200); // Minimum size for squares
        item.w = targetSize;
        item.h = targetSize;
      } else if (isLandscape && maxLandscapeW > 0) {
        // For landscape images, normalize width and calculate height
        const targetWidth = Math.max(maxLandscapeW, 1200);
        item.w = targetWidth;
        item.h = targetWidth / aspectRatio;
      } else if (isPortrait && maxPortraitH > 0) {
        // For portrait images, normalize height and calculate width  
        const targetHeight = Math.max(maxPortraitH, 1200);
        item.h = targetHeight;
        item.w = targetHeight * aspectRatio;
      }
      
      // Round the final values
      item.w = Math.round(item.w);
      item.h = Math.round(item.h);
      
      if (originalW !== item.w || originalH !== item.h) {
        console.log(`Normalized item ${index}:`, {
          type: isSquare ? 'square' : isLandscape ? 'landscape' : 'portrait',
          originalW,
          originalH,
          newW: item.w,
          newH: item.h,
          aspectRatio: item.w / item.h
        });
      }
    });
  }
  
  createVideoHtml(video) {
    // Get video source - check for src attribute first, then source elements
    let videoSrc = video.getAttribute('src');
    let sourceTags = '';
    
    if (!videoSrc) {
      // If no src attribute, look for source elements
      const sources = video.querySelectorAll('source');
      if (sources.length > 0) {
        sources.forEach(source => {
          const src = source.getAttribute('src');
          const type = source.getAttribute('type') || '';
          if (src) {
            sourceTags += `<source src="${src}" type="${type}">`;
          }
        });
      }
    }
    
    const controls = video.hasAttribute('controls');
    const autoplay = video.hasAttribute('autoplay');
    const muted = video.hasAttribute('muted');
    const loop = video.hasAttribute('loop');
    const poster = video.getAttribute('poster');
    
    // Build the video HTML with autoplay enabled
    let videoHtml = `
      <div class="pswp-video-container">
        <video 
          ${videoSrc ? `src="${videoSrc}"` : ''}
          ${poster ? `poster="${poster}"` : ''}
          controls
          autoplay
          muted
          loop
          playsinline
          style="width: 100%; height: 100%; object-fit: contain;"
          preload="metadata"
        >`;
    
    // Add source tags if we have them
    if (sourceTags) {
      videoHtml += sourceTags;
    }
    
    // Add fallback message
    videoHtml += `
          <p>Your browser doesn't support HTML5 video. <a href="${videoSrc || '#'}">Download the video</a> instead.</p>
        </video>
      </div>
    `;
    
    return videoHtml;
  }
  
  createExternalVideoHtml(iframe) {
    let src = iframe.getAttribute('src');
    const width = iframe.getAttribute('width') || '100%';
    const height = iframe.getAttribute('height') || '100%';
    
    // Add autoplay parameters to the URL if not already present
    if (src && !src.includes('autoplay=1')) {
      const separator = src.includes('?') ? '&' : '?';
      src += `${separator}autoplay=1&mute=1`;
      
      // For YouTube videos, also add loop parameter
      if (src.includes('youtube.com') || src.includes('youtu.be')) {
        // Extract video ID for loop parameter (YouTube requires playlist=ID for loop)
        const videoIdMatch = src.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        if (videoIdMatch) {
          src += `&loop=1&playlist=${videoIdMatch[1]}`;
        }
      } else if (src.includes('vimeo.com')) {
        src += '&loop=1';
      }
    }
    
    return `
      <div class="pswp-video-container">
        <iframe 
          src="${src}"
          width="${width}"
          height="${height}"
          style="width: 100%; height: 100%; border: none;"
          allowfullscreen
          allow="autoplay; fullscreen; encrypted-media"
        ></iframe>
      </div>
    `;
  }
  
  getHighResolutionImageUrl(src, srcset) {
    // Try to get highest resolution from srcset
    if (srcset) {
      const srcsetItems = srcset.split(',');
      const lastSrc = srcsetItems[srcsetItems.length - 1].trim().split(' ')[0];
      return lastSrc;
    }
    return src;
  }
  
  openPhotoSwipe(index, items) {
    const pswpElement = document.querySelector('.pswp');
    
    // Show the overlay before opening PhotoSwipe
    const overlay = document.querySelector('.product-zoom-overlay');
    if (overlay) overlay.style.display = 'block';
    
    // Fix for mobile viewport
    const viewportMeta = document.querySelector('meta[name="viewport"]');
    const originalViewport = viewportMeta ? viewportMeta.getAttribute('content') : '';
    
    // Preload current image and adjacent images to reduce layout shift
    const preloadIndices = [
      index, 
      (index + 1) % items.length, 
      (index - 1 + items.length) % items.length
    ];
    
    preloadIndices.forEach(i => {
      const preloadImg = new Image();
      preloadImg.src = items[i].src;
    });
    
    // Configure PhotoSwipe options
    const options = {
      index: index,
      history: false,
      focus: true,
      showAnimationDuration: 300,
      hideAnimationDuration: 300,
      closeOnScroll: false,
      closeOnVerticalDrag: false, // Don't close on vertical drag to improve usability
      captionEl: false,
      fullscreenEl: false,
      zoomEl: true,
      shareEl: false,
      counterEl: true,
      arrowEl: true,
      preloaderEl: true,
      tapToClose: false,
      pinchToClose: false, // Disable pinch to close
      maxSpreadZoom: 3, // Maximum zoom level for pinch gesture
      minZoom: 0.8, // Minimum zoom level - prevent over-shrinking
      maxZoom: 3, // Maximum zoom level
      initialZoomLevel: 'fit', // Ensure images start at fit-to-screen level
      loop: true,
      allowPanToNext: true, // Enable swiping to next slide
      pinchToToggleZoom: true, // Enable pinch to toggle zoom
      allowTouchMove: true, // Ensure touch movement is enabled
      preventSwiping: false, // Don't prevent swiping
      mouseUsed: false, // Better mobile performance with this disabled
      modal: true, // Ensure modal behavior
      bgOpacity: 0.85, // Background opacity
      barsSize: { top: 44, bottom: 0 }, // Bar sizes
      spacing: 0.12, // Add some spacing between slides
      getDoubleTapZoom: function(isMouseClick, item) {
        // Increase zoom level on double tap
        return item.initialZoomLevel < 0.7 ? 2.5 : 1;
      },
      getThumbBoundsFn: (index) => {
        // Fix for Flickity carousel - get the currently visible (selected) image
        // instead of using index directly
        const visibleImage = document.querySelector('.gallery-carousel__cell.is-selected img');
        if (!visibleImage) {
          // If we can't find the visible image, fallback to default behavior
          const img = this.galleryImages[index];
          if (!img) return {x: window.innerWidth/2, y: window.innerHeight/2, w: 10}; // Safe fallback
          
          const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
          const rect = img.getBoundingClientRect();
          return { x: rect.left, y: rect.top + pageYScroll, w: rect.width };
        }
        
        // Use the visible image for animation
        const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
        const rect = visibleImage.getBoundingClientRect();
        
        return { 
          x: rect.left, 
          y: rect.top + pageYScroll, 
          w: rect.width 
        };
      }
    };
    
    // Initialize PhotoSwipe
    const gallery = new PhotoSwipe(pswpElement, PhotoSwipeUI_Default, items, options);
    
    // Store reference for iOS Safari dimension updates
    window.currentPhotoSwipeGallery = gallery;
    
    // Handle different content types
    gallery.listen('gettingData', function(index, item) {
      // Make sure dimensions are set for proper scaling
      if (!item.w || item.w < 100 || !item.h || item.h < 100) {
        if (item.isVideo) {
          item.w = 1200;
          item.h = 675; // 16:9 default
        } else {
        const img = new Image();
        img.onload = function() {
          item.w = this.width;
          item.h = this.height;
          gallery.invalidateCurrItems();
          gallery.updateSize(true);
        };
        img.src = item.src;
        }
      }
    });
    
    // Setup video elements after PhotoSwipe processes them
    const setupVideoElements = () => {
      // Find all video containers and set them up properly
      const videoContainers = gallery.container.querySelectorAll('.pswp-video-container');
      videoContainers.forEach(container => {
        const video = container.querySelector('video');
        const iframe = container.querySelector('iframe');
        
        if (video) {
          console.log('Setting up video element');
          
          // Calculate optimal video size based on container and viewport
          const containerRect = container.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          
          // Set basic styling
          video.style.width = '100%';
          video.style.height = '100%';
          video.style.objectFit = 'contain';
          video.style.objectPosition = 'center';
          video.style.display = 'block';
          video.style.margin = '0 auto';
          
          // Ensure minimum size on mobile
          if (viewportWidth <= 767) {
            video.style.minHeight = Math.max(containerRect.height * 0.8, viewportHeight * 0.5) + 'px';
            video.style.maxHeight = (viewportHeight - 160) + 'px'; // Account for UI elements
          } else {
            video.style.minHeight = Math.max(containerRect.height * 0.9, viewportHeight * 0.6) + 'px';
            video.style.maxHeight = (viewportHeight - 100) + 'px';
          }
          
          // Always try to autoplay videos in PhotoSwipe
          video.play().catch(e => console.log('Video autoplay failed:', e));
        } else if (iframe) {
          console.log('Setting up iframe element');
          
          // Calculate optimal iframe size
          const containerRect = container.getBoundingClientRect();
          const viewportHeight = window.innerHeight;
          const viewportWidth = window.innerWidth;
          
          iframe.style.width = '100%';
          iframe.style.height = '100%';
          iframe.style.display = 'block';
          iframe.style.margin = '0 auto';
          iframe.style.border = 'none';
          
          // Ensure minimum size on mobile
          if (viewportWidth <= 767) {
            iframe.style.minHeight = Math.max(containerRect.height * 0.8, viewportHeight * 0.5) + 'px';
            iframe.style.maxHeight = (viewportHeight - 160) + 'px';
          } else {
            iframe.style.minHeight = Math.max(containerRect.height * 0.9, viewportHeight * 0.6) + 'px';
            iframe.style.maxHeight = (viewportHeight - 100) + 'px';
          }
        }
      });
    };
    
    // Set up videos after PhotoSwipe renders content
    gallery.listen('afterChange', function() {
      // Pause all videos first
      const allVideos = gallery.container.querySelectorAll('video');
      allVideos.forEach(video => {
        video.pause();
      });
      
      // Then setup and play the current video
      setTimeout(() => {
        setupVideoElements();
        
        // Find and play the current slide's video
        const currentIndex = gallery.getCurrentIndex();
        const currentItem = items[currentIndex];
        if (currentItem && currentItem.isVideo) {
          const currentContainer = gallery.container.querySelector('.pswp__item:nth-child(' + (currentIndex + 1) + ')');
          if (currentContainer) {
            const currentVideo = currentContainer.querySelector('video');
            if (currentVideo) {
              currentVideo.play().catch(e => console.log('Video autoplay failed:', e));
            }
          }
        }
      }, 50);
    });
    
    // Also handle the initial load
    gallery.listen('initialZoomIn', function() {
      setTimeout(setupVideoElements, 100);
    });
    
    // Initialize thumbnails after PhotoSwipe is opened
    gallery.listen('initialZoomInEnd', () => {
      // Set up any video elements
      setTimeout(setupVideoElements, 50);
      
      // Then initialize thumbnails
      this.initThumbnailNavigation(gallery, items);
      
      // Force a layout update to ensure everything is positioned correctly
      setTimeout(() => {
        gallery.updateSize(true);
      }, 100);
    });
    
    // Handle window resize for video sizing
    let resizeTimeout;
    const handleVideoResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        setupVideoElements();
        gallery.updateSize(true);
      }, 100);
    };
    
    window.addEventListener('resize', handleVideoResize);
    
    // Clean up event listener when gallery closes
    gallery.listen('close', () => {
      window.removeEventListener('resize', handleVideoResize);
    });
    
    // Additional gesture handling setup
    gallery.listen('initialZoomIn', () => {
      // Ensure proper initialization of zoom
      document.body.classList.add('pswp-open');
      
      // Hide Shopify chat widget
      this.hideShopifyChat();
      
      // Force the body to be non-scrollable
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
      
      // Set viewport to allow zooming
      if (viewportMeta) {
        viewportMeta.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=4.0, user-scalable=1, viewport-fit=cover');
      }
    });
    
    gallery.listen('initialZoomOut', () => {
      // Hide the overlay when closing
      const overlay = document.querySelector('.product-zoom-overlay');
      if (overlay) overlay.style.display = 'none';
      
      document.body.classList.remove('pswp-open');
      
      // Show Shopify chat widget
      this.showShopifyChat();
      
      // Restore scrolling
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Restore viewport
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }
    });
    
    gallery.listen('close', () => {
      // Hide the overlay when closing
      const overlay = document.querySelector('.product-zoom-overlay');
      if (overlay) overlay.style.display = 'none';
      
      document.body.classList.remove('pswp-open');
      
      // Show Shopify chat widget
      this.showShopifyChat();
      
      // Restore scrolling
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      
      // Restore viewport
      if (viewportMeta && originalViewport) {
        viewportMeta.setAttribute('content', originalViewport);
      }
    });
    
    gallery.listen('beforeChange', function() {
      // Update active thumbnail
      const thumbnails = document.querySelectorAll('.pswp__thumbnail');
      thumbnails.forEach((thumb, i) => {
        if (i === gallery.getCurrentIndex()) {
          thumb.classList.add('active');
        } else {
          thumb.classList.remove('active');
        }
      });
      
      // Set the Flickity carousel to the same index when closing
      const flickityInstance = Flickity.data('.gallery-carousel');
      if (flickityInstance) {
        flickityInstance.select(gallery.getCurrentIndex());
      }
      
      // Preload the next image to reduce layout shift during navigation
      const nextIndex = (gallery.getCurrentIndex() + 1) % items.length;
      const preloadImg = new Image();
      preloadImg.src = items[nextIndex].src;
    });
    
    // Handle resize/orientation changes
    const handleGalleryResize = () => {
      gallery.updateSize(true);
    };
    
    window.addEventListener('resize', handleGalleryResize);
    window.addEventListener('orientationchange', handleGalleryResize);
    
    // Remove listeners on destroy
    gallery.listen('destroy', () => {
      window.removeEventListener('resize', handleGalleryResize);
      window.removeEventListener('orientationchange', handleGalleryResize);
      // Clear gallery reference
      window.currentPhotoSwipeGallery = null;
    });
    
    gallery.init();
  }
  
  createPhotoSwipeHtml() {
    // Remove any existing PhotoSwipe elements first to avoid duplicates
    const existingPswp = document.querySelector('.pswp');
    if (existingPswp) {
      existingPswp.remove();
    }
    
    // Create an overlay div that will sit between the page and PhotoSwipe
    const overlayEl = document.createElement('div');
    overlayEl.className = 'product-zoom-overlay';
    overlayEl.style.position = 'fixed';
    overlayEl.style.top = '0';
    overlayEl.style.left = '0';
    overlayEl.style.width = '100vw';
    overlayEl.style.height = '100dvh'; // Use dynamic viewport height
    overlayEl.style.backgroundColor = 'rgba(8, 16, 13, 0.95)';
    overlayEl.style.zIndex = '8999999';
    overlayEl.style.display = 'none';
    document.body.appendChild(overlayEl);
    
    const template = `
      <div class="pswp" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="pswp__bg"></div>
        <div class="pswp__scroll-wrap">
          <div class="pswp__container">
            <div class="pswp__item"></div>
            <div class="pswp__item"></div>
            <div class="pswp__item"></div>
          </div>
          <div class="pswp__ui pswp__ui--hidden">
            <div class="pswp__top-bar">
              <div class="pswp__counter"></div>
              <button class="pswp__button pswp__button--close" title="Close (Esc)"></button>
              <button class="pswp__button pswp__button--zoom" title="Zoom in/out"></button>
              <div class="pswp__preloader">
                <div class="pswp__preloader__icn">
                  <div class="pswp__preloader__cut">
                    <div class="pswp__preloader__donut"></div>
                  </div>
                </div>
              </div>
            </div>
            <div class="pswp__share-modal pswp__share-modal--hidden pswp__single-tap">
              <div class="pswp__share-tooltip"></div>
            </div>
            <button class="pswp__button pswp__button--arrow--left" title="Previous (arrow left)"></button>
            <button class="pswp__button pswp__button--arrow--right" title="Next (arrow right)"></button>
            <div class="pswp__caption">
              <div class="pswp__caption__center"></div>
            </div>
            <!-- Add thumbnails container -->
            <div class="pswp__thumbnails"></div>
          </div>
        </div>
      </div>
    `;
    
    // Append to body directly for maximum z-index effectiveness
    document.body.insertAdjacentHTML('beforeend', template.trim());
  }
  
  handleResize() {
    const wasMobile = this.isMobile;
    this.isMobile = window.innerWidth < 768;
    
    // If device type changed, reinitialize
    if (wasMobile !== this.isMobile) {
      this.initialized = false;
      this.init();
    }
  }

  // Add methods to hide/show Shopify chat widget
  hideShopifyChat() {
    const shopifyChat = document.querySelector('#shopify-chat');
    if (shopifyChat) {
      shopifyChat.style.display = 'none';
      shopifyChat.style.visibility = 'hidden';
      shopifyChat.style.opacity = '0';
      shopifyChat.style.pointerEvents = 'none';
      shopifyChat.style.zIndex = '-1';
    }
    
    // Also hide the chat widget iframe if it exists with a different selector
    const chatWidgetIframe = document.querySelector('#ShopifyChat');
    if (chatWidgetIframe) {
      chatWidgetIframe.style.display = 'none';
      chatWidgetIframe.style.visibility = 'hidden';
      chatWidgetIframe.style.opacity = '0';
      chatWidgetIframe.style.pointerEvents = 'none';
      chatWidgetIframe.style.zIndex = '-1';
    }
  }
  
  showShopifyChat() {
    const shopifyChat = document.querySelector('#shopify-chat');
    if (shopifyChat) {
      shopifyChat.style.display = '';
      shopifyChat.style.visibility = '';
      shopifyChat.style.opacity = '';
      shopifyChat.style.pointerEvents = '';
      shopifyChat.style.zIndex = '';
    }
    
    // Also restore the chat widget iframe if it exists
    const chatWidgetIframe = document.querySelector('#ShopifyChat');
    if (chatWidgetIframe) {
      chatWidgetIframe.style.display = '';
      chatWidgetIframe.style.visibility = '';
      chatWidgetIframe.style.opacity = '';
      chatWidgetIframe.style.pointerEvents = '';
      chatWidgetIframe.style.zIndex = '';
    }
  }

  // Add a new method to initialize the thumbnail navigation
  initThumbnailNavigation(gallery, items) {
    const thumbnailContainer = document.querySelector('.pswp__thumbnails');
    if (!thumbnailContainer) return;
    
    // Clear any existing thumbnails
    thumbnailContainer.innerHTML = '';
    
    // Add thumbnails for each media item
    items.forEach((item, index) => {
      const thumbnail = document.createElement('div');
      thumbnail.className = 'pswp__thumbnail';
      
      // Add video class if this is a video item
      if (item.isVideo) {
        thumbnail.classList.add('video-thumbnail');
      }
      
      if (index === gallery.getCurrentIndex()) {
        thumbnail.classList.add('active');
      }
      
      // Create thumbnail image (preview image for videos, actual image for images)
      const img = document.createElement('img');
      img.src = item.msrc || item.src; // Use thumbnail source if available
      img.alt = 'Thumbnail ' + (index + 1);
      thumbnail.appendChild(img);
      
      // Add click event to navigate to this slide
      thumbnail.addEventListener('click', () => {
        gallery.goTo(index);
      });
      
      thumbnailContainer.appendChild(thumbnail);
    });
    
    // Update thumbnail state when gallery changes
    gallery.listen('beforeChange', () => {
      const currentIndex = gallery.getCurrentIndex();
      const thumbnails = thumbnailContainer.querySelectorAll('.pswp__thumbnail');
      
      thumbnails.forEach((thumb, index) => {
        if (index === currentIndex) {
          thumb.classList.add('active');
        } else {
          thumb.classList.remove('active');
        }
      });
    });
    
    // Make sure thumbnails container is visible
    thumbnailContainer.style.display = 'flex';
  }
}

// Initialize the product zoom functionality with proper timing
let productZoom;

// Wait for the gallery and Flickity to be ready before initializing ProductZoom
let initAttempts = 0;
const maxAttempts = 20; // 4 seconds total (20 * 200ms)

function initProductZoom() {
  initAttempts++;
  console.log(`Attempting ProductZoom initialization... (attempt ${initAttempts}/${maxAttempts})`);
  
  const galleryContainer = document.querySelector('.gallery-carousel');
  const mediaGallery = document.querySelector('media-gallery');
  const galleryCells = galleryContainer ? galleryContainer.querySelector('.gallery-carousel__cell') : null;
  
  console.log('Elements check:');
  console.log('- galleryContainer:', !!galleryContainer, galleryContainer);
  console.log('- mediaGallery:', !!mediaGallery, mediaGallery);
  console.log('- galleryCells:', !!galleryCells, galleryCells);
  
  // Check if Flickity is available globally
  console.log('- Flickity available:', typeof Flickity !== 'undefined');
  
  let flickityInstance = null;
  if (typeof Flickity !== 'undefined' && galleryContainer) {
    flickityInstance = Flickity.data('.gallery-carousel');
    console.log('- flickityInstance:', !!flickityInstance, flickityInstance);
  }
  
  if (galleryContainer && mediaGallery && galleryCells) {
    console.log('✅ Gallery ready, initializing ProductZoom (Flickity optional)');
    productZoom = new ProductZoom();
    productZoom.init(); // Manually trigger initialization
  } else if (initAttempts < maxAttempts) {
    console.log('❌ Gallery not ready, retrying ProductZoom init in 200ms');
    setTimeout(initProductZoom, 200);
  } else {
    console.error('❌ Failed to initialize ProductZoom after', maxAttempts, 'attempts');
    console.log('Final state check:');
    console.log('- galleryContainer:', !!galleryContainer);
    console.log('- mediaGallery:', !!mediaGallery);
    console.log('- galleryCells:', !!galleryCells);
  }
}

// Initialize when DOM is ready, but wait for Flickity
console.log('🚀 Setting up ProductZoom initialization, readyState:', document.readyState);

if (document.readyState === 'loading') {
  console.log('📅 DOM still loading, waiting for DOMContentLoaded');
  document.addEventListener('DOMContentLoaded', () => {
    console.log('📅 DOMContentLoaded fired, starting ProductZoom init in 500ms');
    // Give extra time for Flickity to initialize
    setTimeout(initProductZoom, 500);
  });
} else {
  console.log('📅 DOM already ready, starting ProductZoom init in 500ms');
  // Wait a bit for any dynamic content to load
  setTimeout(initProductZoom, 500);
} 